import React, {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { MemberRaw } from "../../../domain/Member";
import { SessionsFacepile } from "../../SessionsFacepile";
import { VersionSessionsFacepile } from "../VersionSessionsFacepile";

/**
 * @description AppTopNav の表示内容を各ページから AppBaseLayout 直下に "スロット" として流し込む仕組み。
 *
 * 各ページは `<AppTopNavSlot accessMembersRaw={...}>...</AppTopNavSlot>` を render する。
 * children は AppTopNav が用意したコンテナ要素へ createPortal され、AppTopNav 本体は
 * AppBaseLayout 直下で常時 1 回だけ render される。
 *
 * accessMembersRaw を渡すのはシート/ビューのように在席をそのドキュメント単位で出したいページのみ。
 * 未指定のページはバージョン単位の在席 (VersionSessionsFacepile) にフォールバックする。
 *
 * これにより、ページ内で runtime error が発生して ErrorWrapper のフォールバックが描画される状況でも、
 * AppTopNav (＋ menu-btn / アカウントアイコン) を画面上に残せる。
 *
 * state-lifting (useEffect で内容を Provider state に push) ではなく portal を使うことで、
 * - children が毎 render で別参照になり setState が連鎖する問題
 * - 遷移時の cleanup clobber / 一瞬空になるちらつき
 * を避け、children は React ツリー上ページの位置のまま (= ページの context 配下で) 解決される。
 */

type AppTopNavContainers = {
  // children を流し込む先 (PageHeader と subcontent の間)
  childrenContainer: HTMLElement | null;
  // SessionsFacepile を流し込む先 (アカウントアイコンの左)
  facepileContainer: HTMLElement | null;
};

type AppTopNavContainerSetters = {
  setChildrenContainer: (el: HTMLElement | null) => void;
  setFacepileContainer: (el: HTMLElement | null) => void;
};

const noop = () => {};

const AppTopNavContainersContext = createContext<AppTopNavContainers>({
  childrenContainer: null,
  facepileContainer: null,
});
const AppTopNavContainerSettersContext =
  createContext<AppTopNavContainerSetters>({
    setChildrenContainer: noop,
    setFacepileContainer: noop,
  });

export const AppTopNavSlotProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [childrenContainer, setChildrenContainer] =
    useState<HTMLElement | null>(null);
  const [facepileContainer, setFacepileContainer] =
    useState<HTMLElement | null>(null);

  const containers = useMemo(
    () => ({ childrenContainer, facepileContainer }),
    [childrenContainer, facepileContainer]
  );
  // useState の setter は安定参照なので setters オブジェクトも生成時 1 回で固定できる。
  const setters = useMemo(
    () => ({ setChildrenContainer, setFacepileContainer }),
    []
  );

  return (
    <AppTopNavContainerSettersContext.Provider value={setters}>
      <AppTopNavContainersContext.Provider value={containers}>
        {children}
      </AppTopNavContainersContext.Provider>
    </AppTopNavContainerSettersContext.Provider>
  );
};

/**
 * AppTopNav 本体が children / facepile の描画先コンテナを Provider に登録するための
 * callback ref を取得する。
 */
export const useAppTopNavSlotContainerSetters = (): AppTopNavContainerSetters =>
  useContext(AppTopNavContainerSettersContext);

/**
 * 各ページが render する slot コンポーネント。AppTopNav 本体が用意したコンテナへ
 * children と在席 facepile を portal する。
 */
export const AppTopNavSlot: React.FC<{
  children?: ReactNode;
  accessMembersRaw?: MemberRaw[];
}> = ({ children = null, accessMembersRaw }) => {
  const { childrenContainer, facepileContainer } = useContext(
    AppTopNavContainersContext
  );
  return (
    <>
      {childrenContainer ? createPortal(children, childrenContainer) : null}
      {facepileContainer
        ? createPortal(
            accessMembersRaw ? (
              <SessionsFacepile accessMembersRaw={accessMembersRaw} />
            ) : (
              <VersionSessionsFacepile />
            ),
            facepileContainer
          )
        : null}
    </>
  );
};
